import { Directive, OnInit, OnDestroy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { InlineTableData } from '@morningstar/shared/models';
import { confirm } from '@morningstar/shared/operators';
import { merge, of, Subject } from 'rxjs';
import { mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { InlineTableService } from '../base-service/inline-table-service';
import { TableComponent } from './table-component';

@Directive()
export abstract class InlineTableComponent<T extends InlineTableData> extends TableComponent<T> implements OnInit, OnDestroy {

  // == CONSTANTS ========================================================

  useBaseGetDataMethod = true;
  dataDescription = 'entry';

  // == STATE ============================================================

  data$ = this.service.data$;

  tableReloaded = new Subject();
  tableReloaded$ = this.tableReloaded.asObservable();

  saveSuccess = new Subject();
  saveSuccess$ = this.saveSuccess.asObservable();

  updateSuccess = new Subject();
  updateSuccess$ = this.updateSuccess.asObservable();

  // == ACTIONS ==========================================================

  getData = new Subject<void>();
  getData$ = this.getData.asObservable();

  delete = new Subject<[T, number]>();
  delete$ = this.delete.asObservable();

  add = new Subject<void>();
  add$ = this.add.asObservable();

  save = new Subject<[T, number]>();
  save$ = this.save.asObservable();

  saveAll = new Subject();
  saveAll$ = this.saveAll.asObservable();

  form = this.formBuilder.group({
    data: this.formBuilder.array([])
  });

  // == SIDE EFFECTS =====================================================

  onGetData$ = this.getData$.pipe(
    tap(() => this.service.getInlineData())
  );

  onDataLoaded$ = this.data$.pipe(
    tap(data => {
      this.formArray.clear();
      data.forEach(d => this.formArray.push(this.createFormGroup(d)));
      this.reloadTableData();
    })
  );

  onDelete$ = this.delete$.pipe(
    confirm(_ => `Confirm deleting ${this.dataDescription}?`, this.dialog, true),
    withLatestFrom(this.delete$),
    mergeMap(([_, [data, index]]) => {
      if (data.new) {
        this.formArray.removeAt(index);
        this.reloadTableData();
        return of(true);
      } else {
        return this.service.deleteInlineData(data.id).pipe(
          tap(() => {
            this.formArray.removeAt(index);
            this.reloadTableData();
          })
        );
      }
    })
  );

  onSaveAll$ = this.saveAll$.pipe(
    mergeMap(() => {
      return this.service.saveAllInlineData(this.formArray.getRawValue()).pipe(
        tap(data => {
          this.formArray.clear();
          data.forEach(datum => this.formArray.push(this.createFormGroup(datum)));
          this.reloadTableData();
          this.saveSuccess.next();
        })
      );
    })
  );

  onAdd$ = this.add$.pipe(
    tap(() => {
      this.addDefaultGroupToFormArray();
      this.dataSource.data = this.formArray.getRawValue();
    })
  );

  onSave$ = this.save$.pipe(
    mergeMap(([data, index]) => {
      if (data.new) {
        return this.service.addInlineDataAndUpdateId(data).pipe(
          tap(createdData => {
            if (createdData && createdData.id) {
              this.formArray.at(index).patchValue({ ...createdData });
            }

            this.formArray.at(index).get('new').setValue(false);
            data.new = false;

            this.dataSource.data[index] = this.formArray.at(index).value;
            this.dataSource._updateChangeSubscription();
            this.saveSuccess.next();
          })
        );
      } else {
        return this.service.updateInlineData(data).pipe(
          tap(() => {
            if ((<any>data).editing) {
              (<any>data).editing = false;
              this.formArray.at(index).patchValue({ editing: false });
            }
            
            this.dataSource.data[index] = this.formArray.at(index).value;
            this.dataSource._updateChangeSubscription();
            this.updateSuccess.next();
          })
        );
      }
    })
  );

  // == SUBSCRIPTION =====================================================

  subscriptions = merge(
    this.onGetData$,
    this.onAdd$,
    this.onDataLoaded$,
    this.onSave$,
    this.onDelete$,
    this.onSaveAll$
  ).subscribe();

  // == LIFECYCLE ========================================================

  constructor(protected service: InlineTableService<T>,
              protected dialog: MatDialog,
              protected formBuilder: FormBuilder) {
    super(service);
    // We need to reset the data source because if we navigate away from the page
    // and get back, the data source will still have data in it and it will try
    // to fetch a form control.
    this.dataSource.data = [];
  }

  ngOnInit() {
    if (this.useBaseGetDataMethod) {
      this.getData.next();
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  // == HELPERS +========================================================

  reloadTableData() {
    this.dataSource.data = this.formArray.getRawValue();
    this.tableReloaded.next();
  }

  abstract createFormGroup(data: T);
  abstract addDefaultGroupToFormArray();

  get formArray(): FormArray {
    return this.form.get('data') as FormArray;
  }

  getFormArrayIndexRawValue(index: number) {
    return (this.formArray.at(index) as FormGroup).getRawValue();
  }
}
